import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useFormikContext } from 'formik';
import type { FormikValues } from 'formik/dist/types';
import iban from 'iban';
import { isEqual, omit, pick, set, some } from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';

import { useClaim } from '~/components/ClaimContainer';
import { isVatEnabled } from '~/components/communications/ContactUtils';
import {
  FINANCE_TAB_KEY,
  FORMIK_FINANCE_FIELD_IDS,
  SUPPORTED_IBAN_LANGUAGES,
  TRUSTED_PAYEE_ACTIVE_STATUS,
  TRUSTED_PAYEE_FIELD_IDS,
} from '~/components/Contacts/UpsertContact/Tabs/Finance/constants';
import { getFullFinancePath } from '~/components/Contacts/UpsertContact/Tabs/Finance/utils';
import type { Tab } from '~/components/Contacts/UpsertContact/types';
import {
  getDocumentInitialValues,
  getDocumentPayload,
  getDocumentValidationSchema,
} from '~/components/Contacts/UpsertContact/UploadDocument/utils';
import { useContact } from '~/components/Contacts/UpsertContact/useContact';
import { PERMISSION_ACTIONS } from '~/components/core/Permissions/PermissionUtils';
import { isValidNzBankAccount } from '~/components/exposures/PaymentRequestContainer/paymentContainerUtils';
import { useCms } from '~/components/hooks/useCms';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import useDataFetcher from '~/components/useDataFetcher';
import CONTACT_FINANCE_CHANGE_REASONS from '~/server_shared/generated-types/CONTACT_FINANCE_CHANGE_REASONS';
import type { VerificationDetailsResponse } from '~/server_shared/openapi/data-contracts';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

import ChangeReason from '../../ChangeReason';
import { getInitializedValues, getReasonValidationSchema } from '../../utils';
import TabWrapper from '../TabWrapper';

import Bank from './Bank';
import BankProofDocument from './BankProofDocument';
import FinanceVerificationBanner from './FinanceVerificationBanner';
import TrustedPayee from './TrustedPayee';
import Vat from './Vat';

const FinanceTab: React.FC = () => {
  const { isSubmitting } = useFormikContext();
  const { isCreation, originalContact } = useContact();
  const { user } = useCms();
  const { isRequireVerificationForContactFinanceEdit } = usePaymentsConfiguration();
  const { documentUploadPercent } = useFinanceTab();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  // locallyApproved is needed because after approval, originalContact is not refetched
  // (the dialog doesn't refresh contact data). This ensures UI shows "Approved" even if
  // the verification details refetch fails. Reset when financeChangeUlid changes.
  const [locallyApproved, setLocallyApproved] = useState(false);

  // Derive verification status from contact fields
  const financeChangeUlid = originalContact?.finance_change_ulid;

  // Fetch verification details when we have a change_ulid
  const shouldFetchDetails = Boolean(financeChangeUlid && isRequireVerificationForContactFinanceEdit && !isCreation);
  const {
    data: verificationDetails,
    reloadData: reloadVerificationDetails,
    isLoading: isLoadingDetails,
  } = useDataFetcher<VerificationDetailsResponse>(
    shouldFetchDetails ? `/api/v1/contact_finance_verification/${financeChangeUlid}` : null,
    null,
    { shouldFetch: shouldFetchDetails, shouldReportAxiosError: false }
  );

  // Use locallyApproved to immediately reflect approval, or check API response status
  // (handles case where another user approved and contact data is stale)
  const isApproved =
    locallyApproved || (originalContact?.is_finance_verified ?? false) || verificationDetails?.status === 'approved';
  // Only show pending if we're loading (safe default) or status is actually 'pending'.
  // Note: 'declined' status shouldn't normally be returned here because when a verification
  // is superseded, the contact's finance_change_ulid is updated to point to the new pending
  // verification. A declined status would only occur with stale contact data, in which case
  // we show no banner rather than a broken "Awaiting approval" banner.
  const hasPendingVerification = Boolean(
    financeChangeUlid && !isApproved && (isLoadingDetails || verificationDetails?.status === 'pending')
  );

  const handleApprove = async () => {
    if (!financeChangeUlid) return;

    setIsApproving(true);
    try {
      await axios.post(`/api/v1/contact_finance_verification/approve/${financeChangeUlid}`);

      // Update local state immediately - ensures UI shows approved even if refetch fails
      setLocallyApproved(true);

      // Re-fetch verification details to get approver info (best effort)
      await reloadVerificationDetails();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  // Reset edit mode and local approval state when verification changes
  // (e.g., after save creates new verification, or another user edits)
  useEffect(() => {
    setIsEditMode(false);
    setLocallyApproved(false);
  }, [financeChangeUlid]);

  const isFormDisabled =
    isRequireVerificationForContactFinanceEdit && (hasPendingVerification || isApproved) && !isEditMode;

  // Check if this is a self-approval attempt (user trying to approve their own request)
  // Also treat as self-request while loading to prevent brief enabled state
  const isSelfRequest =
    hasPendingVerification && (isLoadingDetails || verificationDetails?.requesting_user_id === user.id);

  const sharedProps = {
    disabled: isSubmitting || isFormDisabled,
  };

  const showBanner =
    isRequireVerificationForContactFinanceEdit && !isCreation && (hasPendingVerification || isApproved) && !isEditMode;

  return (
    <TabWrapper>
      <div className="mb-32 grid grid-cols-1 gap-20">
        {/* The backend applies `trusted_payee` as its own PATCH section, outside the finance
            verification path, so the verification lock must not reach it — a user with
            TRUSTED_PAYEE_ARRANGEMENT can still manage the arrangement while VAT/bank are locked. */}
        {!isCreation ? <TrustedPayee disabled={isSubmitting} /> : null}
        {showBanner && (
          <FinanceVerificationBanner
            hasPendingVerification={hasPendingVerification}
            isApproved={isApproved}
            requestingUser={verificationDetails?.requesting_user ?? null}
            requestDateTime={verificationDetails?.request_datetime ?? null}
            approvingUser={verificationDetails?.deciding_user ?? null}
            approvalDateTime={verificationDetails?.decision_datetime ?? null}
            onApprove={handleApprove}
            onEdit={handleEdit}
            isApproving={isApproving}
            isSelfRequest={isSelfRequest}
          />
        )}
        <Vat {...sharedProps} />
        <Bank {...sharedProps} />
        <BankProofDocument {...sharedProps} documentUploadPercent={documentUploadPercent} />
        {!isCreation ? (
          <ChangeReason
            reasonFieldId={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.REASON)}
            explanationFieldId={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.EXPLANATION)}
            reasonsDict={CONTACT_FINANCE_CHANGE_REASONS}
            {...sharedProps}
          />
        ) : null}
      </div>
    </TabWrapper>
  );
};

const useFinanceTab = (): Tab => {
  // No Formik Context
  const [documentUploadPercent, setDocumentUploadPercent] = useState(0);
  const { originalContact, contactId, isCreation, currentCountry } = useContact();
  const { claim } = useClaim();
  const { userOrganization } = useCms();
  const {
    isUkBankTransferEnabledForAny,
    isAuBankTransferEnabledForAny,
    isIbanBankTransferEnabledForAny,
    isNzBankTransferEnabledForAny,
    isTrustedPayeeEnabled,
  } = usePaymentsConfiguration();

  const getInitialValues = (contact?: ContactFullModel) => {
    const values = {};

    const setInitialValue = (fieldId: string, initialValue: unknown, defaultInitialValue: unknown = '') =>
      set(values, fieldId, initialValue ?? defaultInitialValue);

    const documentInitialValues = getDocumentInitialValues(FORMIK_FINANCE_FIELD_IDS.BANK_PROOF_DOCUMENT_ID, claim);
    Object.keys(documentInitialValues).forEach((formikFieldId) =>
      setInitialValue(formikFieldId, documentInitialValues[formikFieldId])
    );

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.IS_VAT_REGISTERED, contact?.is_vat_registered, null);
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.VAT_NUMBER, contact?.vat_number);

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.IBAN_LANGUAGE, contact?.language);
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.IBAN, contact?.iban);
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.SWIFT_BIC, contact?.swift_bic);

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.UK_BANK_SORT_CODE, contact?.uk_sort_code);
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.UK_BANK_ACCOUNT_NUMBER, contact?.uk_bank_account_number);

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.AU_BSB_NUMBER, contact?.au_bsb_number);
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.AU_BANK_ACCOUNT_NUMBER, contact?.au_bank_account_number);

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.NZ_BANK_ACCOUNT_NUMBER, contact?.nz_bank_account_number);

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.ACCOUNT_DETAILS, contact?.account_details);

    setInitialValue(
      FORMIK_FINANCE_FIELD_IDS.IS_TRUSTED_PAYEE,
      contact?.trusted_payee_status === TRUSTED_PAYEE_ACTIVE_STATUS,
      false
    );
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_VALID_TO, contact?.trusted_payee_valid_to);
    setInitialValue(FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_PER_PAYMENT_LIMIT, contact?.trusted_payee_per_payment_limit);

    setInitialValue(FORMIK_FINANCE_FIELD_IDS.REMOVE_BANK_PROOF_DOCUMENT, false);

    if (!isCreation) {
      setInitialValue(FORMIK_FINANCE_FIELD_IDS.REASON, '');
      setInitialValue(FORMIK_FINANCE_FIELD_IDS.EXPLANATION, '');
    }

    return values;
  };

  const getValidationSchema = () => {
    const validations = {};

    const addValidation = (fieldId: string, fieldValidation?: Yup.AnySchema) =>
      set(validations, fieldId, fieldValidation);

    const documentValidationSchema = getDocumentValidationSchema(
      FORMIK_FINANCE_FIELD_IDS.BANK_PROOF_DOCUMENT_ID,
      true,
      claim
    );
    Object.entries(documentValidationSchema).forEach(([formikFieldId, validation]) => {
      addValidation(formikFieldId, validation);
    });

    addValidation(FORMIK_FINANCE_FIELD_IDS.IS_VAT_REGISTERED, Yup.bool().nullable());
    addValidation(FORMIK_FINANCE_FIELD_IDS.VAT_NUMBER, Yup.string());

    addValidation(FORMIK_FINANCE_FIELD_IDS.IBAN_LANGUAGE, Yup.string().oneOf(SUPPORTED_IBAN_LANGUAGES.concat('other')));
    addValidation(
      FORMIK_FINANCE_FIELD_IDS.IBAN,
      Yup.string().test('finance-iban', 'Invalid IBAN', (value) => !value || iban.isValid(value))
    );
    addValidation(
      FORMIK_FINANCE_FIELD_IDS.SWIFT_BIC,
      Yup.string().test(
        'finance-swift-bic',
        'Invalid SWIFT/BIC code',
        (value) => !value || /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/i.test(value)
      )
    );

    addValidation(
      FORMIK_FINANCE_FIELD_IDS.UK_BANK_SORT_CODE,
      Yup.string().test(
        'finance-uk-sort-code',
        'Sort code must be of the form 20-00-00 or 200000',
        (sort_code) => !sort_code || /^\d{2}-\d{2}-\d{2}$/.test(sort_code) || /^\d{6}$/.test(sort_code)
      )
    );
    addValidation(
      FORMIK_FINANCE_FIELD_IDS.UK_BANK_ACCOUNT_NUMBER,
      Yup.string().matches(/^\d{8}$/, 'Account number must be 8 digits')
    );

    addValidation(
      FORMIK_FINANCE_FIELD_IDS.AU_BSB_NUMBER,
      Yup.string().test(
        'finance-au-bsb-number',
        'BSB number must be of the form 200-000 or 200000',
        (bsb_number) => !bsb_number || /^\d{3}-\d{3}$/.test(bsb_number) || /^\d{6}$/.test(bsb_number)
      )
    );
    addValidation(
      FORMIK_FINANCE_FIELD_IDS.AU_BANK_ACCOUNT_NUMBER,
      Yup.string().matches(/^\d{6,10}$/, 'Account number must be between 6 to 10 digits')
    );

    addValidation(
      FORMIK_FINANCE_FIELD_IDS.NZ_BANK_ACCOUNT_NUMBER,
      Yup.string().test(
        'finance-nz-bank-account',
        'NZ bank account must be of the form 01-0001-1234567-00 or 010001123456700 (15-16 digits)',
        // Contact finance is org-scoped, so it always uses the default 15-16 range; the stricter
        // exact-16 rule is enforced per sub-org at payment time.
        (value) => isValidNzBankAccount(value)
      )
    );

    // Free text — only enforce the DB length limit.
    addValidation(
      FORMIK_FINANCE_FIELD_IDS.ACCOUNT_DETAILS,
      Yup.string().max(1024, 'Account details cannot exceed 1024 characters')
    );

    addValidation(
      FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_PER_PAYMENT_LIMIT,
      Yup.number()
        .nullable()
        // An empty limit is a valid "no cap" arrangement (the backend UpdateTrustedPayeeSection
        // treats a null per_payment_limit as no cap), so normalize '' to null instead of letting
        // Yup cast it to NaN. Not required: an existing no-cap arrangement is prefilled as
        // checkbox-true with an empty limit, and must stay saveable when editing other fields.
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .when(FORMIK_FINANCE_FIELD_IDS.IS_TRUSTED_PAYEE, {
          is: true,
          then: Yup.number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError('Must be a number')
            .positive('Must be greater than 0'),
        })
    );

    const originalValidTo = originalContact?.trusted_payee_valid_to ?? null;
    addValidation(
      FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_VALID_TO,
      Yup.string()
        .nullable()
        .test('trusted-payee-valid-to-not-past', 'Valid to cannot be in the past', function (value) {
          // Exempt the prefilled original date only while the arrangement stays inactive (checkbox
          // unchecked) — that date is never sent, so it must not block unrelated finance edits.
          // Reactivating re-checks the box, so a past valid_to must fail inline here rather than
          // reach the backend's _regrant and come back as a 400.
          const isTrustedPayee = Boolean(this.parent[FORMIK_FINANCE_FIELD_IDS.IS_TRUSTED_PAYEE]);
          if (!value || (!isTrustedPayee && value === originalValidTo)) {
            return true;
          }
          // Compare in UTC: the backend rejects on `valid_to < utcnow().date()`, so a local-time
          // comparison would accept today's local date for a user whose local date is behind UTC's
          // and then fail server-side with a 400.
          return moment.utc(value).isSameOrAfter(moment.utc().startOf('day'));
        })
    );

    if (!isCreation) {
      addValidation(
        FORMIK_FINANCE_FIELD_IDS.REASON,
        Yup.string().test(
          'finance-reason',
          'Required',
          // Compare finance fields only: the change reason belongs to the finance section, and a
          // trusted-payee-only edit never sends one (it PATCHes its own section instead).
          getReasonValidationSchema(originalContact, getFinanceValuesToCompare, getInitialValues)
        )
      );
      addValidation(
        FORMIK_FINANCE_FIELD_IDS.EXPLANATION,
        Yup.string().when(FORMIK_FINANCE_FIELD_IDS.REASON, {
          is: 'other',
          then: Yup.string().required('Required'),
          otherwise: Yup.string(),
        })
      );
    }

    return validations;
  };

  // Includes the trusted-payee fields so a trusted-payee-only edit still marks the tab dirty and
  // reaches getPreparedPayload — UpsertContactFormik skips tabs this reports as unchanged.
  const getValuesToCompare = (values: FormikValues) => {
    const omittedValues = omit(values, [FORMIK_FINANCE_FIELD_IDS.REASON, FORMIK_FINANCE_FIELD_IDS.EXPLANATION]);
    return getInitializedValues(omittedValues);
  };

  // The finance fields proper — everything that goes into the verification-gated `finance` PATCH
  // section. The trusted-payee arrangement is applied as its own section and is excluded.
  const getFinanceValuesToCompare = (values: FormikValues) => omit(getValuesToCompare(values), TRUSTED_PAYEE_FIELD_IDS);

  const haveFinanceFieldsChanged = (values: FormikValues) => {
    const initialValues = pick(getInitialValues(originalContact), Object.keys(values));
    return !isEqual(getFinanceValuesToCompare(initialValues), getFinanceValuesToCompare(values));
  };

  const isHiddenForContact = () => {
    // Hidden Tabs cannot have absolute require fields, only conditionally required fields
    const vatEnabled = isVatEnabled(currentCountry);

    return !some(
      [
        vatEnabled,
        isUkBankTransferEnabledForAny,
        isAuBankTransferEnabledForAny,
        isIbanBankTransferEnabledForAny,
        isNzBankTransferEnabledForAny,
        // The trusted-payee panel lives in this tab and is independent of VAT and the bank-transfer
        // methods, so without it a US contact in an org with no bank transfer configured would have
        // the whole tab filtered out and the panel unreachable. Scoped to edit, mirroring the
        // panel's own !isCreation render gate, so it never unhides an otherwise-empty create tab.
        !isCreation && isTrustedPayeeEnabled,
      ],
      Boolean
    );
  };

  // Formik keeps blank optional fields as '', which the backend date/decimal parsers reject.
  const nullIfBlank = (value: unknown) => (value === '' ? null : (value ?? null));

  const buildTrustedPayeeSection = (values: FormikValues) => {
    const isTrustedPayee = Boolean(values[FORMIK_FINANCE_FIELD_IDS.IS_TRUSTED_PAYEE]);
    return {
      is_trusted_payee: isTrustedPayee,
      valid_to: isTrustedPayee ? nullIfBlank(values[FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_VALID_TO]) : null,
      per_payment_limit: isTrustedPayee
        ? nullIfBlank(values[FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_PER_PAYMENT_LIMIT])
        : null,
    };
  };

  const getPreparedPayload = async (values: FormikValues) => {
    // Trusted-payee write is only supported on edit, and only when the org has the feature enabled
    // (mirrors the panel's isTrustedPayeeEnabled gate). During creation, or when the flag is off,
    // drop the fields entirely rather than sending a trusted_payee section — the backend rejects it
    // with a 400 unconditionally while the flag is off, even for a no-op is_trusted_payee=false.
    const isTrustedPayeeWriteSupported = !isCreation && isTrustedPayeeEnabled;

    // The tab is dirty whenever the arrangement changed, but `trusted_payee` is applied as its own
    // PATCH section. Sending the finance fields alongside an unchanged finance form would open a
    // finance verification and demand a change reason for a trusted-payee-only edit, so emit the
    // arrangement on its own. The nesting under `finance` is what the permission sanitizer keys on;
    // EditContactDialog hoists it to the top level afterwards.
    if (isTrustedPayeeWriteSupported && !haveFinanceFieldsChanged(values)) {
      return { trusted_payee: buildTrustedPayeeSection(values) };
    }

    const payload = (await getDocumentPayload(
      values,
      userOrganization.id,
      contactId,
      setDocumentUploadPercent
    )) as Record<string, unknown>;

    if (isTrustedPayeeWriteSupported) {
      payload.trusted_payee = buildTrustedPayeeSection(values);
    }
    delete payload[FORMIK_FINANCE_FIELD_IDS.IS_TRUSTED_PAYEE];
    delete payload[FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_VALID_TO];
    delete payload[FORMIK_FINANCE_FIELD_IDS.TRUSTED_PAYEE_PER_PAYMENT_LIMIT];

    return payload;
  };

  const fieldsPermissionActions = {
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.IS_VAT_REGISTERED)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.VAT_NUMBER)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.IBAN_LANGUAGE)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.IBAN)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.SWIFT_BIC)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.UK_BANK_SORT_CODE)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.UK_BANK_ACCOUNT_NUMBER)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.AU_BSB_NUMBER)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.AU_BANK_ACCOUNT_NUMBER)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.NZ_BANK_ACCOUNT_NUMBER)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.ACCOUNT_DETAILS)]: PERMISSION_ACTIONS.CONTACT_FINANCE_PII,
    [getFullFinancePath('trusted_payee')]: PERMISSION_ACTIONS.TRUSTED_PAYEE_ARRANGEMENT,
  };

  return {
    label: 'Finance',
    tabKey: FINANCE_TAB_KEY,
    tabComponent: FinanceTab,
    getInitialValues,
    getValidationSchema,
    getValuesToCompare,
    isHiddenForContact,
    documentUploadPercent,
    getPreparedPayload,
    fieldsPermissionActions,
  };
};

export default useFinanceTab;
